import { Comments as _Comments, OnCreateCommentProps, OnDeleteCommentProps, OnToggleReactionProps, OnUpdateCommentProps } from '@/components/element/Comments';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useEffect, useState } from 'react';
import { useSalesOrderComments, useSalesOrderCommentsInvalidate } from '@/hooks/api/1310_sales_order_comments';
import { useCreateSalesOrderComment } from '@/hooks/api/1320_create_sales_order_comment';
import { useSalesOrderCommentReaction } from '@/hooks/api/1321_sales_order_comment_reactions';
import { MentionData } from '@draft-js-plugins/mention';
import { usePurchaseOrderTargetUsers } from '@/hooks/api/0541_purchase_order_target_users';
import { useSalesOrderTargetUsers } from '@/hooks/api/1231_sales_order_target_users';
import { useUpdateSalesOrderComment } from '@/hooks/api/1322_update_sales_order_comment';
import { useDeleteSalesOrderComment } from '@/hooks/api/1323_delete_sales_order_comment';

export const Comments = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const [mentions, setMentions] = useState<MentionData[]>([]);
  const { data: comments } = useSalesOrderComments({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { data: members } = useSalesOrderTargetUsers({ identifier: _w_!.identifier, team_id: _t_!.id, id: _so_!.id });
  const { mutate } = useCreateSalesOrderComment();
  const { mutate: updateMutate } = useUpdateSalesOrderComment();
  const { mutate: deleteMutate } = useDeleteSalesOrderComment();
  const { invalidate: invalidateComments } = useSalesOrderCommentsInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _so_!.id });
  const { mutate: reactionMutate } = useSalesOrderCommentReaction();

  useEffect(() => {
    if (members) {
      setMentions(() =>
        members.map((d) => ({
          id: d.id,
          name: d.user_account.name,
          avatar: d.user_account.icon_file_url,
        }))
      );
    }
  }, [members]);

  const onCreateComment = ({ content, content_json, setComment, setError, parent_id }: OnCreateCommentProps) => {
    mutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: _so_!.id,
        content: content!,
        content_json: content_json,
        parent_id: parent_id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onUpdateComment = ({ id, content, content_json, setComment, setError }: OnUpdateCommentProps) => {
    updateMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: _so_!.id,
        id: id,
        content: content!,
        content_json: content_json,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onDeleteComment = ({ id, setComment, setError }: OnDeleteCommentProps) => {
    deleteMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: _so_!.id,
        id: id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
          invalidateComments();
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onToggleReaction = ({ comment_id, emoji_id, content, setComment, setError }: OnToggleReactionProps) => {
    reactionMutate(
      { identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: _so_!.id, id: comment_id, emoji_id, content },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => setError(error),
        onSettled: () => {},
      }
    );
  };

  if (mentions.length == 0) return <></>;

  return (
    <_Comments
      identifier={`sales_order_${_w_!.identifier}_${_t_!.id}_${_so_!.id}`}
      comments={comments}
      user={me!}
      mentions={mentions}
      onCreateComment={onCreateComment}
      onToggleReaction={onToggleReaction}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
    />
  );
};
